import axios from 'axios';
import { DataSource } from '../models/models';

interface CollectionData {
    totalElements: number, content: DataSource[]
}

class CollectionService {
  private isLoading = false;
  private cachedContent: CollectionData | null = null;
  private cacheTimestamp: number | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000;
  private pendingRequest: Promise<CollectionData> | null = null;

  async fetchCollections(page = 0, size = 1000, omitCache = false): Promise<CollectionData> {
    if (this.cachedContent && this.cacheTimestamp) {
      const now = Date.now();
      if ((now - this.cacheTimestamp < this.CACHE_DURATION) && !omitCache) {
        return this.cachedContent;
      }
    }

    if (this.pendingRequest) {
      return this.pendingRequest;
    }

    this.isLoading = true;

    this.pendingRequest = (async () => {
      try {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('size', String(size));

        const response = await axios.get('/documents/by-collection', { params });
        const data = { totalElements: response.data?.totalElements || 0, content: response.data?.content || []};

        this.cachedContent = data;
        this.cacheTimestamp = Date.now();

        return data;
      } finally {
        this.isLoading = false;
        this.pendingRequest = null;
      }
    })();

    return this.pendingRequest;
  }

  async updateSourceName(sourceId: number, newName: string): Promise<void> {
    await axios.patch(`/collections/${sourceId}/name`, null, {
      params: { collectionName: newName }
    });
    this.invalidateCache();
  }

  async deleteSource(sourceId: number): Promise<void> {
    await axios.delete(`/collections/${sourceId}/documents`);
    this.invalidateCache();
  }

  async deleteDocument(documentId: number): Promise<void> {
    await axios.delete(`/documents/${documentId}`);
    this.invalidateCache();
  }

  invalidateCache() {
    this.cachedContent = null;
    this.cacheTimestamp = null;
  }

  isRequestInProgress(): boolean {
    return this.isLoading;
  }
}

export const collectionService = new CollectionService();
